/** @typedef {'brown' | 'white'} DicePlayer */
/** @typedef {'light' | 'dark'} CheckerColor */

/**
 * @typedef {object} DiceRollPayload
 * @property {string} rollId
 * @property {DicePlayer} player
 * @property {[number, number]} values
 * @property {number} [durationMs]
 * @property {string} [userId]
 */

/**
 * @typedef {object} PiecePickPayload
 * @property {string} userId
 * @property {string} fromZone
 * @property {number} stackIndex
 * @property {CheckerColor} color
 */

/**
 * @typedef {object} PieceHoverPayload
 * @property {string} userId
 * @property {string} toZone
 */

/**
 * @typedef {object} PieceMovePayload
 * @property {string} userId
 * @property {string} fromZone
 * @property {number} stackIndex
 * @property {CheckerColor} color
 * @property {string} toZone
 * @property {boolean} reverted
 */

/**
 * @typedef {object} GameChannelHandlers
 * @property {(row: Record<string, unknown>) => void} [onGameRow]
 * @property {(payload: DiceRollPayload) => void} [onDiceRoll]
 * @property {(payload: PiecePickPayload) => void} [onPiecePick]
 * @property {(payload: PieceHoverPayload) => void} [onPieceHover]
 * @property {(payload: PieceMovePayload) => void} [onPieceMove]
 */

/**
 * @param {unknown} message
 * @returns {Record<string, unknown> | null}
 */
function unwrapBroadcastPayload(message) {
  if (!message || typeof message !== "object") return null;
  const m = /** @type {Record<string, unknown>} */ (message);
  if (m.payload && typeof m.payload === "object") {
    return /** @type {Record<string, unknown>} */ (m.payload);
  }
  return m;
}

/**
 * Single Realtime channel per game: postgres row sync + broadcast presence.
 * Uses the public `game:{id}` topic (same as postgres_changes) so delivery matches
 * the channel that already works for board state sync.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} gameId
 * @param {string} currentUserId
 * @param {GameChannelHandlers} handlers
 */
export async function subscribeGame(supabase, gameId, currentUserId, handlers) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (token) {
    await supabase.realtime.setAuth(token);
  }

  const {
    data: { subscription: authSub },
  } = supabase.auth.onAuthStateChange((_event, session) => {
    if (session?.access_token) {
      void supabase.realtime.setAuth(session.access_token);
    }
  });

  const channel = supabase.channel(`game:${gameId}`);

  let subscribed = false;
  /** @type {{ event: string; payload: Record<string, unknown> }[]} */
  const pending = [];

  /**
   * @param {string} event
   * @param {Record<string, unknown>} payload
   */
  async function flushSend(event, payload) {
    const status = await channel.send({
      type: "broadcast",
      event,
      payload,
    });
    if (status === "error" || status === "timed out") {
      console.warn("game broadcast send failed", event, status);
    }
  }

  channel
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "games", filter: `id=eq.${gameId}` },
      (payload) => {
        if (payload.new && typeof payload.new === "object") {
          handlers.onGameRow?.(/** @type {Record<string, unknown>} */ (payload.new));
        }
      }
    )
    .on("broadcast", { event: "dice_roll" }, (message) => {
      const payload = unwrapBroadcastPayload(message);
      if (!payload) return;
      const p = /** @type {DiceRollPayload} */ (payload);
      if (p.userId === currentUserId) return;
      handlers.onDiceRoll?.(p);
    })
    .on("broadcast", { event: "piece_pick" }, (message) => {
      const payload = unwrapBroadcastPayload(message);
      if (!payload) return;
      const p = /** @type {PiecePickPayload} */ (payload);
      if (p.userId === currentUserId) return;
      handlers.onPiecePick?.(p);
    })
    .on("broadcast", { event: "piece_hover" }, (message) => {
      const payload = unwrapBroadcastPayload(message);
      if (!payload) return;
      const p = /** @type {PieceHoverPayload} */ (payload);
      if (p.userId === currentUserId) return;
      handlers.onPieceHover?.(p);
    })
    .on("broadcast", { event: "piece_move" }, (message) => {
      const payload = unwrapBroadcastPayload(message);
      if (!payload) return;
      const p = /** @type {PieceMovePayload} */ (payload);
      if (p.userId === currentUserId) return;
      handlers.onPieceMove?.(p);
    });

  await new Promise((resolve) => {
    channel.subscribe((status, err) => {
      if (status === "SUBSCRIBED") {
        subscribed = true;
        for (const msg of pending) {
          void flushSend(msg.event, msg.payload);
        }
        pending.length = 0;
        resolve(undefined);
      } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
        console.warn("game channel subscribe failed", status, err);
        resolve(undefined);
      }
    });
  });

  /**
   * @param {string} event
   * @param {Record<string, unknown>} payload
   */
  function send(event, payload) {
    const body = { ...payload, userId: currentUserId };
    if (!subscribed) {
      pending.push({ event, payload: body });
      return;
    }
    void flushSend(event, body);
  }

  return {
    send,
    destroy() {
      authSub.unsubscribe();
      void supabase.removeChannel(channel);
    },
  };
}
